import { AuthorizedICDTreatingDepartments, CareplanFilter, CareplanStatusFilter, RoDashboardTabs } from 'op-enums';
import { BaseConfigInterface } from './types';
import { BOOLEAN_OPTIONS, UK_CIED_OPTIONS } from '../TreatmentInfoPage/Interface';

const { CROMWELL } = AuthorizedICDTreatingDepartments;

const uk_config: BaseConfigInterface = {
  showPreferredName: true,
  SpecialPhysicsConsultVisible: false,
  ErrorPopup: {
    Generic:
      'We encountered an error. Please refresh the page. If the issue persists, please contact GenesisCare IT support on 0800 160 1027',
    Practitioner:
      'Your account setup appears to be incomplete or missing information. Please contact GenesisCare IT support on 0800 160 1027',
    Patient: 'We encountered an error. Please refresh the page. If the issue persists, please contact your clinic.',
  },
  ReferringPhysicians: {
    oncologist: 'Responsible Physician',
    generalPractitioner: 'Primary Physician (GP)',
    referringSurgeon: 'Referring Clinician',
    referringSurgeon2: 'Referring Other',
    referringSurgeon3: 'Referring Other',
  },
  DefaultValue: {
    cied: '',
    patientStatus: 'outPatient',
    previousRadioTreatment: '',
    treatmentRORequired: '',
    tumourName: 'tumour ',
  },
  Clinex: {
    betaTesting: false,
  },
  Diagnosis: {
    defaultMStageTypeFilter: 'Clinical',
  },
  Intake: {
    disabled: true,
    isProstateDisabled: true,
    breastMandatoryFields: ['grade', 'er', 'pgr', 'her2'],
    lungMandatoryFields: ['weightLoss', 'smokingHistory', 'egfr', 'alk', 'pdl1'],
    headNeckMandatoryFields: ['P16', 'smokingHistory'],
    lymphomaMandatoryFields: ['stage', 'aOrBSymptoms', 'extranodal', 'bulkDisease'],
    breastFormVersion: 1,
    breastFormShowExercise: true,
  },
  PatientSummary: {
    isVisible: true,
    labsEnabled: false,
    RODocumentUpload: true,
    documentFilterField: 'description',
    hideInsuranceTab: false,
    summaryLabel: 'Patient Chart',
  },
  OutcomesPage: {
    isVisible: false,
    DateFormat: 'DD-MMM-YYYY',
    DateTimeFormat: 'DD MMM YYYY h:mma',
  },
  OTRPage: {
    isVisible: false,
    DateFormat: 'DD-MMM-YYYY',
    DateTimeFormat: 'DD MMM YYYY h:mma',
  },
  DisplayButton: {
    displaySimButton: false,
  },
  DisplayModal: {
    submission: true,
  },
  SimulationPage: {
    SpacerGelWarning: true,
    imagingText: 'Add imaging request',
    imagingCountTitle: 'Imaging',
    disableExtentDefined: true,
    disablePositionSetup: true,
    immobilisationLabel: 'Immobilisation',
  },
  TreatmentInfoPage: {
    mandatoryFields: ['intent', 'treatingDepartment', 'readyForCareDate', 'cied', 'previousRadioTreatment'],
    ReadyForCareDate: true,
    AdditionalBookingInfo: true,
    MDT: true,

    AuthorizedICDTreatingDepartments: [CROMWELL],
    Hormones: true,
    Brachytherapy: true,
    ccip: false,
    TreatmentStartDate: true,
    ConsentStatus: false,
    PharmacyPrescription: false,
    PerformanceStatus: true,
  },
  PrescriptionPage: {
    MicrodosimetryTld: false,
    WeeklyPhysicsCheck: false,
    BasicDoseCalculation: false,
  },
  SubmissionPage: {
    PlanAimSubmissionType: 'Plan Aim',
    PlanAimHidden: false,
  },
  VolumingPage: {
    UsePrioritizedOARs: false,
    TargetValueDoseSuffixEnabled: false,
  },
  RadiationDashboard: {
    dateTimeFormat: 'DD/MM/YYYY hh:mm A',
    active: RoDashboardTabs.CLINIC_LIST,
    careplanDashboard: {
      careplanStatusFilters: [
        { id: CareplanStatusFilter.DRAFT, name: CareplanStatusFilter.DRAFT, checked: true },
        { id: CareplanStatusFilter.PLAN_AIM, name: CareplanStatusFilter.PLAN_AIM, checked: true },
        { id: CareplanStatusFilter.PRESCRIPTION, name: CareplanStatusFilter.PRESCRIPTION, checked: true },
      ],
      careplanFilters: [{ id: CareplanFilter.UNSUBMITTED, name: CareplanFilter.UNSUBMITTED, checked: false }],
      isPatientSummaryLinkEnabled: true,
    },
    showFilters: false,
  },
  QuestionLabel: {
    CIED: 'Cardiac Device',
    RoRequired: 'Clinical Oncologist required',
    GoldSeeds: 'Fiducials',
    phase: 'Phase',
  },
  QuestionOptions: {
    CIED: UK_CIED_OPTIONS,
    previousRadioTreatment: BOOLEAN_OPTIONS,
  },
  QuestionValidationChildren: {
    CIED: ['ciedPatientDependancy', 'ciedLinacRequired'],
    Staging: {
      Primary: {
        RO: ['laterality', 'diagnosisDate'],
        MO: ['laterality'],
      },
      Metastasis: {
        RO: ['laterality', 'diagnosisDate'],
        MO: [],
      },
    },
  },
  ResetMutationVariableDefaults: {
    CIED: {
      // Yes, there is a spelling mistake with Dependency
      ciedPatientDependancy: '',
      ciedLinacRequired: '',
    },
    CiedProtocolRequired: {},
  },
  GetMutationVariableDefaults: {
    CIED: {
      // Yes, there is a spelling mistake with Dependency
      ciedPatientDependancy: '',
      ciedLinacRequired: '',
    },
    CiedProtocolRequired: {},
  },
  DoseSiteSummary: {
    enabled: false,
    DateFormat: 'DD/MM/YYYY',
  },
  CardioReferralField: false,
  Tooltips: {
    hasPriority: false,
    hasClinicalPathologicalStaging: false,
  },
  UseDocumentTimezone: false,
  DefaultTimezone: 'Europe/London',
  DateFormat: 'DD MM YYYY',
  ShortDateFormat: 'DD/MM/YYYY',
  DiagnosisDateFormat: 'DD-MMM-YYYY',
  CareplanVoidDescription: 'Documentation will be voided and a clinic note will be added into Mosaiq.',
  EmailMaxLength: 40,
  Outage: {
    link: '',
    showBanner: true,
    message:
      'Mosaiq will be upgraded to version 3.2.4.1 from 1pm Friday 25th September to 10am Saturday 26th September 2026. Horizon may be unavailable during the upgrade.',
    campaignId: 'mosaiq-upgrade-3-2-4-1',
    startDate: '2026-09-18T09:00',
    endDate: '2026-09-26T10:00',
  },
  Notes: {
    DateTimeFormat: 'DD/MM/YYYY hh:mm A',
  },
  DefaultLocale: 'en-gb',
  SupportEmail: 'itsupport@genesiscare.com.au',
  ShowResetDemoDataMenuLink: false,
  PatientCard: {
    appointmentDateFormat: 'ddd, Do MMM YYYY',
    defaultDateFormat: 'DD-MMM-YYYY',
    defaultAlternateDateFormat: 'DD MMM YYYY',
    dateOfBirthFormat: 'Do MMM YYYY',
  },
  AISummary: {
    lastUpdatedDateTimeFormat: 'DD MMM YYYY, h:mm A',
  },
};

export default uk_config;
